import { DEFAULT_DATE_FORMAT } from '@sophocles/app/constants';
import { mapListToRecords, normalizeList, websocketsHandlerFactory } from '@sophocles/entities/entityUtils';
import {
  RelayControllerStatusHistory,
  relayControllerStatusHistorySchema,
} from '@sophocles/entities/RelayControllerStatusHistory/relayControllerStatusHistoryRecord';
import apiClient, { vantageApiClient } from '@sophocles/http-common';
import { checkHttpStatus, generateQueryParams, genericErrorAction } from '@sophocles/utils';
import { endOfDay, format, parseISO, startOfDay } from 'date-fns';
import { normalize } from 'normalizr';

/* Receive Actions */
export const RECEIVE_RELAY_CONTROLLER_STATUS_HISTORIES = 'RECEIVE_RELAY_CONTROLLER_STATUS_HISTORIES';
function receiveRelayControllerStatusHistories(json) {
  return {
    type: RECEIVE_RELAY_CONTROLLER_STATUS_HISTORIES,
    relayControllerStatusHistories: mapListToRecords(json, RelayControllerStatusHistory),
  };
}

export const RECEIVE_LIMITED_RELAY_CONTROLLER_STATUS_HISTORIES = 'RECEIVE_LIMITED_RELAY_CONTROLLER_STATUS_HISTORIES';
function receiveLimitedRelayControllerStatusHistories(json) {
  return {
    type: RECEIVE_LIMITED_RELAY_CONTROLLER_STATUS_HISTORIES,
    relayControllerStatusHistories: mapListToRecords(json, RelayControllerStatusHistory),
  };
}

/* Request Actions */
// starter request
export const REQUEST_RELAY_CONTROLLER_STATUS_HISTORY = 'REQUEST_RELAY_CONTROLLER_STATUS_HISTORY';
function requestRelayControllerStatusHistory(relayControllerStatusHistory_id) {
  return {
    type: REQUEST_RELAY_CONTROLLER_STATUS_HISTORY,
    relayControllerStatusHistory_id,
  };
}

export const REQUEST_RELAY_CONTROLLER_STATUS_HISTORY_FOR_UNIT_WITH_RANGE =
  'REQUEST_RELAY_CONTROLLER_STATUS_HISTORY_FOR_UNIT_WITH_RANGE';
function requestRelayControllerStatusHistoryForUnitWithRange(unit, from, to) {
  return {
    type: REQUEST_RELAY_CONTROLLER_STATUS_HISTORY_FOR_UNIT_WITH_RANGE,
    unit,
    from,
    to,
  };
}

/* Fetch Actions */
// starter fetch
function normalizeVantageResponesStatusHistory(rawJson) {
  const normalizedResponse = {
    id: rawJson.history_id,
    unit_id: rawJson.smartlink_unit,
    relay_controller: rawJson.smartlink_unit,
    receive_time: rawJson.last_receive_time,
    status_type: 14,
    ...rawJson,
    ...rawJson.status,
  };
  return normalizedResponse;
}

export function fetchRelayControllerStatusHistory(relayControllerStatusHistory_id, isVantage) {
  const client = isVantage ? vantageApiClient : apiClient;
  let url = `/relaycontrollerstatushistory/${relayControllerStatusHistory_id}/`;
  if (isVantage) {
    url = `/stat/history/${relayControllerStatusHistory_id}/`;
  }

  if (isVantage) {
    return dispatch => {
      dispatch(requestRelayControllerStatusHistory(relayControllerStatusHistory_id));
      return client
        .get(url)
        .then(checkHttpStatus)
        .then(response => {
          return normalizeVantageResponesStatusHistory(response.data, relayControllerStatusHistory_id);
        })
        .then(data => {
          return normalize(data, relayControllerStatusHistorySchema);
        })
        .then(json => dispatch(receiveRelayControllerStatusHistories(json.entities.relayControllerStatusHistories)))
        .catch(error => dispatch(genericErrorAction(error, REQUEST_RELAY_CONTROLLER_STATUS_HISTORY)));
    };
  }

  return dispatch => {
    dispatch(requestRelayControllerStatusHistory(relayControllerStatusHistory_id));
    return client
      .get(url)
      .then(checkHttpStatus)
      .then(response => {
        return normalizeVantageResponesStatusHistory(response.data, relayControllerStatusHistory_id);
      })
      .then(data => {
        return normalize(data, relayControllerStatusHistorySchema);
      })
      .then(json => dispatch(receiveRelayControllerStatusHistories(json.entities.relayControllerStatusHistories)))
      .catch(error => dispatch(genericErrorAction(error, REQUEST_RELAY_CONTROLLER_STATUS_HISTORY)));
  };
}

function normalizeVantageResponesForUnitWithRange(rawJson) {
  const normalizedResponse = {
    results: rawJson.results.map(result => {
      return {
        id: result.history_id,
        receive_time: result.last_receive_time,
        relay_controller: result.smartlink_unit,
        status_type: 14,
        ...result,
        ...result.status,
      };
    }),
  };
  return normalizedResponse;
}

export function fetchRelayControllerStatusHistoriesForUnitWithRange(unit_id, from, to, isVantage) {
  const fromTS = startOfDay(parseISO(from));
  const toTS = endOfDay(parseISO(to));

  const fromString = encodeURIComponent(format(fromTS, DEFAULT_DATE_FORMAT));
  const toString = encodeURIComponent(format(toTS, DEFAULT_DATE_FORMAT));

  const client = isVantage ? vantageApiClient : apiClient;

  let url = `/relaycontrollerstatushistory/currents/?relay_controller=${unit_id}`;
  if (isVantage) {
    url = `/stat/history/?smartlink_unit=${unit_id}`;
  }

  if (isVantage) {
    return dispatch => {
      dispatch(requestRelayControllerStatusHistoryForUnitWithRange(unit_id, fromTS, toTS));
      return client
        .get(`${url}&receive_time__date__gte=${fromString}&receive_time__date__lte=${toString}&limit=10000`)
        .then(checkHttpStatus)
        .then(response => normalizeVantageResponesForUnitWithRange(response.data))
        .then(rawJson =>
          normalizeList(rawJson.results, 'relayControllerStatusHistories', relayControllerStatusHistorySchema),
        )
        .then(json =>
          dispatch(receiveLimitedRelayControllerStatusHistories(json.entities.relayControllerStatusHistories)),
        )
        .catch(error => {
          dispatch(genericErrorAction(error, REQUEST_RELAY_CONTROLLER_STATUS_HISTORY_FOR_UNIT_WITH_RANGE));
        });
    };
  }
  return dispatch => {
    dispatch(requestRelayControllerStatusHistoryForUnitWithRange(unit_id, fromTS, toTS));
    return client
      .get(`${url}&receive_time__date__gte=${fromString}&receive_time__date__lte=${toString}&limit=10000`)
      .then(checkHttpStatus)
      .then(response =>
        normalizeList(response.data.results, 'relayControllerStatusHistories', relayControllerStatusHistorySchema),
      )
      .then(json =>
        dispatch(receiveLimitedRelayControllerStatusHistories(json.entities.relayControllerStatusHistories)),
      )
      .catch(error => {
        dispatch(genericErrorAction(error, REQUEST_RELAY_CONTROLLER_STATUS_HISTORY_FOR_UNIT_WITH_RANGE));
      });
  };
}

export function fetchRawRelayControllerStatusHistories(unitId, isVantage, filterParams) {
  const client = isVantage ? vantageApiClient : apiClient;

  let url = '/relaycontrollerstatushistory/currents/';
  const params = generateQueryParams(filterParams);

  if (isVantage) {
    url = '/stat/history/';
    params.smartlink_unit = unitId;
  } else {
    params.relay_controller = unitId;
  }

  return dispatch => {
    return client
      .get(url, { params })
      .then(checkHttpStatus)
      .then(response => {
        if (isVantage) {
          const { data } = response;
          data.results = data.results.map(r => normalizeVantageResponesStatusHistory(r));
          return data;
        }

        return response.data;
      });
  };
}

/* Edit Actions */
export const EDIT_RELAY_CONTROLLER_STATUS_HISTORY = 'EDIT_RELAY_CONTROLLER_STATUS_HISTORY';
export function editRelayControllerStatusHistory(relayControllerStatusHistory_id) {
  return {
    type: EDIT_RELAY_CONTROLLER_STATUS_HISTORY,
    relayControllerStatusHistory: relayControllerStatusHistory_id,
  };
}

export function patchRelayControllerStatusHistory(relayControllerStatusHistory_id, values, auth) {
  return dispatch => {
    dispatch(editRelayControllerStatusHistory(relayControllerStatusHistory_id));
    return apiClient
      .patch(`/relaycontrollerstatushistory/${relayControllerStatusHistory_id}/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, relayControllerStatusHistorySchema))
      .then(json => {
        dispatch(receiveRelayControllerStatusHistories(json.entities.relayControllerStatusHistories));
      })
      .catch(error => {
        dispatch(genericErrorAction(error, EDIT_RELAY_CONTROLLER_STATUS_HISTORY));
      });
  };
}

/* Create Actions */
export const CREATE_RELAY_CONTROLLER_STATUS_HISTORY = 'CREATE_RELAY_CONTROLLER_STATUS_HISTORY';
function sayCreateRelayControllerStatusHistory(values) {
  return {
    type: CREATE_RELAY_CONTROLLER_STATUS_HISTORY,
    values: values,
  };
}

export function createRelayControllerStatusHistory(values, auth) {
  return dispatch => {
    dispatch(sayCreateRelayControllerStatusHistory(values));
    return apiClient
      .post(`/relaycontrollerstatushistory/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, relayControllerStatusHistorySchema))
      .then(json => {
        dispatch(receiveRelayControllerStatusHistories(json.entities.relayControllerStatusHistories));
      })
      .catch(error => {
        dispatch(genericErrorAction(error, CREATE_RELAY_CONTROLLER_STATUS_HISTORY));
      });
  };
}

/* Delete Actions */
export const DELETE_RELAY_CONTROLLER_STATUS_HISTORY = 'DELETE_RELAY_CONTROLLER_STATUS_HISTORY';
function sayDeleteRelayControllerStatusHistory(relayControllerStatusHistory_id) {
  return {
    type: DELETE_RELAY_CONTROLLER_STATUS_HISTORY,
    relayControllerStatusHistory: relayControllerStatusHistory_id,
  };
}

export function deleteRelayControllerStatusHistory(relayControllerStatusHistory_id, auth) {
  return dispatch =>
    apiClient
      .delete(`/relaycontrollerstatushistory/${relayControllerStatusHistory_id}/`)
      .then(checkHttpStatus)
      .then(r => dispatch(sayDeleteRelayControllerStatusHistory(relayControllerStatusHistory_id)))
      .catch(error => {
        dispatch(genericErrorAction(error, DELETE_RELAY_CONTROLLER_STATUS_HISTORY));
      });
}

const receiver = (dispatch, json) =>
  dispatch(receiveRelayControllerStatusHistories(json.entities.relayControllerStatusHistories));

export const handleRelayControllerStatusHistorySocketActions = websocketsHandlerFactory(
  receiver,
  sayDeleteRelayControllerStatusHistory,
  relayControllerStatusHistorySchema,
);
